import { useState, useEffect } from "react";
import { useUsers } from "../../contexts/UsersContext";
import { useApproverModule } from "../../contexts/ApproverModuleContext";
import { useApprovalLevel } from "../../contexts/ApprovalLevelContext";

const EmployeeApproverForm = ({
  loading,
  closeModal,
  onSubmit,
  initialValues,
  errors = {}, // Added errors prop to display validation messages
}) => {
  // const [formData, setFormData] = useState(initialValues);
  const { allUsersWithEmployeeIds, fetchAllUsersWithEmployeeIds } = useUsers();
  const { approverModules, fetchApproverModules } = useApproverModule();
  const { approvalLevels, fetchApprovalLevel } = useApprovalLevel();
  
  const [formData, setFormData] = useState(() => ({
    ...initialValues,
    is_active: initialValues.is_active === '1' || initialValues.is_active === 1, // true if 1, false if 0
  }));
  const [formErrors, setFormErrors] = useState({}); // Validation errors

  useEffect(() => {
    setFormData({
      ...initialValues,
      is_active: initialValues.is_active === '1' || initialValues.is_active === 1,
    });
  }, [initialValues]);

  useEffect(() => {
    fetchAllUsersWithEmployeeIds();
    fetchApproverModules();
    fetchApprovalLevel();
  }, []);

  useEffect(() => {
    // Guard: need option lists
    if (!initialValues) return;
    const hasUsers = Array.isArray(allUsersWithEmployeeIds) && allUsersWithEmployeeIds.length > 0;
    const hasModules = Array.isArray(approverModules) && approverModules.length > 0;
    const hasLevels = Array.isArray(approvalLevels) && approvalLevels.length > 0;
    if (!hasUsers && !hasModules && !hasLevels) return;

    const normalized = {};

    // Employee: initial may be name or employee_id string; select expects employee_id
    if (initialValues.employee_id) {
      const raw = initialValues.employee_id;
      if (hasUsers) {
        const matchUser = allUsersWithEmployeeIds.find(u => u.name === raw || String(u.employee_id) === String(raw));
        if (matchUser) normalized.employee_id = matchUser.employee_id; // select value
      }
    }

    // Approver Module: initial may come as approval_module_id (name) or module_id (id)
    if (initialValues.module_id) {
      normalized.module_id = initialValues.module_id;
    } else if (initialValues.approval_module_id && hasModules) {
      const raw = initialValues.approval_module_id;
      const matchModule = approverModules.find(m => m.display_name === raw || String(m.id) === String(raw));
      if (matchModule) normalized.module_id = matchModule.id;
    }

    // Approval Level: initial may come as approval_level_id (name) or level_id (id)
    if (initialValues.level_id) {
      normalized.level_id = initialValues.level_id;
    } else if (initialValues.approval_level_id && hasLevels) {
      const raw = initialValues.approval_level_id;
      const matchLevel = approvalLevels.find(l => l.name === raw || String(l.id) === String(raw));
      if (matchLevel) normalized.level_id = matchLevel.id;
    }

    // Primary Approver: initial may be name or ID; select expects user.id
    if (initialValues.primary_approver_id && hasUsers) {
      const raw = initialValues.primary_approver_id;
      const matchUser = allUsersWithEmployeeIds.find(u => String(u.id) === String(raw) || u.name === raw || String(u.employee_id) === String(raw));
      if (matchUser) normalized.primary_approver_id = matchUser.id;
    }

    // Secondary Approver: same mapping
    if (initialValues.secondary_approver_id && hasUsers) {
      const raw = initialValues.secondary_approver_id;
      const matchUser = allUsersWithEmployeeIds.find(u => String(u.id) === String(raw) || u.name === raw || String(u.employee_id) === String(raw));
      if (matchUser) normalized.secondary_approver_id = matchUser.id;
    }

    if (Object.keys(normalized).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...normalized,
        is_active: initialValues.is_active === '1' || initialValues.is_active === 1,
      }));
    }
  }, [initialValues, allUsersWithEmployeeIds, approverModules, approvalLevels]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear any existing errors for this field when changed
    if (formErrors[name] || errors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validate = () => {
    const validationErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const onlyTextRegex = /^[A-Za-z\s-]+$/;

    // if (!formData.key.trim()) {
    //   validationErrors.key = "Name is required.";
    // } else if (!onlyTextRegex.test(formData.key.trim())) {
    //   validationErrors.key = "Name should contain only letters and spaces.";
    // }

    // if (!formData.display_name.trim()) {
    //   validationErrors.display_name = "Display name is required.";
    // } else if (!onlyTextRegex.test(formData.display_name.trim())) {
    //   validationErrors.display_name = "Display name should contain only letters and spaces.";
    // }

    return validationErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }	

    setFormErrors({});

    const submissionData = new FormData(); // Use FormData for file upload
    submissionData.append("employee_id", formData.employee_id);
    submissionData.append("module_id", formData.module_id);
    submissionData.append("stage", formData.stage); 
    submissionData.append("level_id", formData.level_id);
    submissionData.append("primary_approver_id", formData.primary_approver_id); 
    submissionData.append("secondary_approver_id", formData.secondary_approver_id); 
    submissionData.append("effective_from", formData.effective_from); 
    submissionData.append("effective_to", formData.effective_to); 
    submissionData.append("is_active", formData.is_active ? 1 : 0); // Send 0 or 1 instead of true/false

    onSubmit(submissionData); 
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 font-poppins">
        {/* Employee ID field */}
        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Employee ID <span className="text-red-500">*</span>
            </label>
            <select
              name="employee_id"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              value={formData.employee_id || ""}
              onChange={handleChange}
            >
              <option value="">Select Employee</option>
              {allUsersWithEmployeeIds.map((user) => (
                <option key={user.id} value={user.employee_id}>
                  {user.name} ({user.employee_id})
                </option>
              ))}
            </select>
            {formErrors.employee_id && (
              <p className="text-red-500 text-sm mt-1">{formErrors.employee_id}</p>
            )}
        </div>

        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Approver Module <span className="text-red-500">*</span>
            </label>
            <select
              name="module_id"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              value={formData.module_id || ""}
              onChange={handleChange}
            >
              <option value="">Select Approver Module</option>
              {approverModules.map((module) => (
                <option key={module.id} value={module.id}>
                  {module.display_name}
                </option>
              ))}
            </select>
            {formErrors.approval_module_id && (
              <p className="text-red-500 text-sm mt-1">{formErrors.approval_module_id}</p>
            )}
        </div>

        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Approver Level <span className="text-red-500">*</span>
            </label>
            <select
              name="level_id"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              value={formData.level_id || ""}
              onChange={handleChange}
            >
              <option value="">Select Approval Level</option>
              {approvalLevels.map((level) => (
                <option key={level.id} value={level.id}>
                  {level.name}
                </option>
              ))}
            </select>
            {formErrors.approval_level_id && (
              <p className="text-red-500 text-sm mt-1">{formErrors.approval_level_id}</p>
            )}
        </div>

        {/* Display Name Field */}
        {/* <div>
            <label htmlFor="stage" className="block dark:text-white text-sm font-medium text-gray-700">
            Stage <span className="text-red-500 ml-1">*</span>
            </label>
            <input
            type="text"
            id="stage"
            name="stage"
            value={formData.stage}
            onChange={handleChange}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
                formErrors.stage ? "border-red-500" : "border-gray-300"
            }`}
            required
            />
            {formErrors.stage && <p className="text-red-500 text-sm mt-1">{formErrors.stage}</p>}
        </div> */}

        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Primary Approver <span className="text-red-500">*</span>
            </label>
            <select
              name="primary_approver_id"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              value={formData.primary_approver_id || ""}
              onChange={handleChange}
            >
              <option value="">Select HOD</option>
              {allUsersWithEmployeeIds.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.employee_id})
                </option>
              ))}
            </select>
            {formErrors.primary_approver_id && (
              <p className="text-red-500 text-sm mt-1">{formErrors.primary_approver_id}</p>
            )}
        </div>

        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Secondary Approver <span className="text-red-500">*</span>
            </label>
            <select
              name="secondary_approver_id"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              value={formData.secondary_approver_id || ""}
              onChange={handleChange}
            >
              <option value="">Select HOD</option>
              {allUsersWithEmployeeIds.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.employee_id})
                </option>
              ))}
            </select>
            {formErrors.secondary_approver_id && (
              <p className="text-red-500 text-sm mt-1">{formErrors.secondary_approver_id}</p>
            )}
        </div>

        {/* Effective from Field */}
        {/* <div>
            <label htmlFor="effective_from" className="block dark:text-white text-sm font-medium text-gray-700">
            Effective From <span className="text-red-500 ml-1">*</span>
            </label>
            <input
            type="date"
            id="effective_from"
            name="effective_from"
            value={formData.effective_from}
            onChange={handleChange}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
                formErrors.effective_from ? "border-red-500" : "border-gray-300"
            }`}
            required
            />
            {formErrors.effective_from && <p className="text-red-500 text-sm mt-1">{formErrors.effective_from}</p>}
        </div> */}

        {/* Effetive to Field */}
        {/* <div>
            <label htmlFor="effective_to" className="block dark:text-white text-sm font-medium text-gray-700">
            Effective To <span className="text-red-500 ml-1">*</span>
            </label>
            <input
            type="date"
            id="effective_to"
            name="effective_to"
            value={formData.effective_to}
            onChange={handleChange}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
                formErrors.effective_to ? "border-red-500" : "border-gray-300"
            }`}
            required
            />
            {formErrors.effective_to && <p className="text-red-500 text-sm mt-1">{formErrors.effective_to}</p>}
        </div> */}

        {/* Is Active Field */}
        <div className="flex items-center">
            <input
            type="checkbox"
            id="is_active"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm font-medium text-gray-700 dark:text-white">
            Is Active
            </label>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end items-center gap-2">
            <button
            type="button"
            onClick={closeModal}
            className="px-6 py-2 font-medium text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
            >
            Cancel
            </button>
            <button
            type="submit"
            className="flex items-center gap-2 px-8 py-2 font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
            {initialValues.id ? "Update" : "Add"}
            {loading && (
                <svg
                className="animate-spin h-5 w-5 ml-2 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                >
                <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                ></circle>
                <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
                </svg>
            )}
            </button>
        </div>
    </form>
  );
};

export default EmployeeApproverForm;
